import type { ActionTree, GetterTree, MutationTree } from "vuex";
import type { ActionFilter, EventFilter } from "@/components/event-action/filter/FilterTypes";
import type { ArchiveRange } from "@/graphql";
import type { DataTableSortMeta } from "@/components/shared/dataTableTypes";
import type { EABFilterType } from "@/graphql";

export type SearchListFilterCacheEntry = {
  search: string;
  searchOptionsFuzzy: boolean;
  selected: string[];
  sortDesc?: number;
  defaultSortKey?: string;
};

export type EABFilterCacheEntry = {
  eventFilters: EventFilter[];
  actionFilters: ActionFilter[];
  eventFilterType?: EABFilterType;
  actionFilterType?: EABFilterType;
  quickSearch?: string;
  archiveRange?: ArchiveRange;
  includeArchive?: boolean;
};

const localState = {
  searchList: {} as Record<
    string,
    SearchListFilterCacheEntry | Record<number, SearchListFilterCacheEntry>
  >,
  eab: {} as Record<number, EABFilterCacheEntry>,
  bdeView: {} as Record<string, Record<number, DataTableSortMeta[]>>,
  internalTypeFilters: {} as Record<string, boolean>,
};

const actions: ActionTree<typeof localState, any> = {
  updateSearchListFilter(context, payload) {
    context.commit("setSearchListFilter", payload);
  },
  updateEABFilter(context, payload) {
    context.commit("setEABFilter", payload);
  },
  updateBDEViewFilter(context, payload) {
    context.commit("setBDEViewFilter", payload);
  },
  updateInternalTypeFilter(context, payload: { key: string; showInternalTypes: boolean }) {
    context.commit("setInternalTypeFilter", payload);
  },
};

const mutations: MutationTree<typeof localState> = {
  setSearchListFilter(
    state,
    payload: { key: string; siteId?: number; filterCache: SearchListFilterCacheEntry },
  ) {
    if (payload.siteId == null) {
      state.searchList[payload.key] = payload.filterCache;
    } else {
      if (state.searchList[payload.key] == null) {
        state.searchList[payload.key] = {};
      }
      (state.searchList[payload.key] as Record<number, SearchListFilterCacheEntry>)[
        payload.siteId
      ] = payload.filterCache;
    }
  },
  setEABFilter(state, payload: { boardId: number; filterCache: EABFilterCacheEntry }) {
    state.eab[payload.boardId] = payload.filterCache;
  },
  setBDEViewFilter(
    state,
    payload: { key: string; tpId: number; filterCache: DataTableSortMeta[] },
  ) {
    if (state.bdeView[payload.key] == null) {
      state.bdeView[payload.key] = {};
    }
    (state.bdeView[payload.key] as Record<number, DataTableSortMeta[]>)[payload.tpId] =
      payload.filterCache;
  },
  setInternalTypeFilter(state, payload: { key: string; showInternalTypes: boolean }) {
    state.internalTypeFilters[payload.key] = payload.showInternalTypes;
  },
};

const getters: GetterTree<typeof localState, any> = {
  getSearchListFilter: (state) => (key: string, siteId?: number) => {
    const cache = state.searchList[key];
    if (siteId == null || cache == null) {
      // If there is no siteId, we return the cache, also if it is null.
      return cache;
    }
    return (cache as Record<number, SearchListFilterCacheEntry>)[siteId];
  },
  getEABFilter: (state) => (boardId: number) => state.eab[boardId],
  getBDEViewFilter: (state) => (key: string, tpId: number) => {
    const cache = state.bdeView[key];
    if (cache == null) {
      return null;
    }
    return (cache as Record<number, DataTableSortMeta[]>)[tpId];
  },
  getInternalTypeFilter: (state) => (key: string) => state.internalTypeFilters[key] ?? false,
};

export default {
  namespaced: true,
  state: localState,
  actions,
  mutations,
  getters,
};
